import { defineStore } from "pinia";
import { computed, ComputedRef, ref, watch } from "vue";
import { useSessionStorage } from "@vueuse/core";
import { TKid } from "stores/useKidsStore";
import {
  DashboardResponseSchedule,
  JournalListResponse,
  JournalResponse,
  StatusReportListResponse,
} from "src/generated/formeapi";
import useFormeServer from "src/composables/server/useFormeServer";
import { isString } from "src/types/typeGuard";

export type KidInfoFormType = {
  firstName: string;
  lastName: string;
  birth: string;
  gender: string;
  phone: string;
};

export type AddKidType = KidInfoFormType & {
  entryUUID: string;
  licenseCode: string;
  exID: string;
  isPhoneValidate: boolean;
};

export type AppendixType = {
  birth: string;
  class: number;
  grade: number;
  gender: string;
  lastName: string;
  firstName: string;
  schoolName: string;
  fullName: string;
};

export const useKidStore = defineStore("kid", () => {
  const kid = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kid`,
    {} as TKid
  );

  const getKid: ComputedRef<TKid> = computed(() => {
    return { ...kid.value };
  });

  function setKid(kidData: TKid) {
    kid.value = kidData;
  }

  function resetKid() {
    kid.value = {} as TKid;
  }

  // 아이 등록시 사용
  const newKid = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/newKid`,
    {
      firstName: "",
      lastName: "",
      birth: "",
      gender: "M",
      phone: "",
      licenseCode: "",
      exID: "",
      entryUUID: "",
      isPhoneValidate: false,
    } as AddKidType
  );

  const getNewKid: ComputedRef<AddKidType> = computed(() => {
    return { ...newKid.value };
  });

  function setNewKid(kidData: AddKidType) {
    newKid.value = kidData;
  }

  function resetNewKid() {
    newKid.value = {
      firstName: "",
      lastName: "",
      birth: "",
      gender: "M",
      phone: "",
      licenseCode: "",
      exID: "",
    } as AddKidType;
  }

  // 아이의 기분
  const kidEmotions = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidEmotions`,
    [] as JournalResponse[]
  );

  const getKidEmotions: ComputedRef<JournalResponse[]> = computed(() => {
    return [...kidEmotions.value];
  });

  function setKidEmotions(journalListResponse: JournalListResponse) {
    kidEmotions.value = journalListResponse.groups?.reduce(
      (previousValue, currentValue) =>
        previousValue.concat(currentValue.journals),
      [] as JournalResponse[]
    );
  }

  function resetKidEmotions() {
    kidEmotions.value = [] as JournalResponse[];
  }

  // 아이 task
  const kidSchedules = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidSchedules`,
    [] as DashboardResponseSchedule[]
  );

  const getKidSchedules: ComputedRef<DashboardResponseSchedule[]> = computed(
    () => {
      return [...kidSchedules.value];
    }
  );

  function setKidSchedules(kidSchedulesData: DashboardResponseSchedule[]) {
    kidSchedules.value = kidSchedulesData;
  }

  function resetKidSchedules() {
    kidSchedules.value = {} as DashboardResponseSchedule[];
  }

  // 아이 Report
  const kidReports = useSessionStorage(
    `forme_b2c_${process.env.VUE_APP_ENV}/kidReports`,
    {} as StatusReportListResponse
  );

  const getKidReports: ComputedRef<StatusReportListResponse> = computed(() => {
    return { ...kidReports.value };
  });

  function resetKidReports() {
    kidReports.value = {} as StatusReportListResponse;
  }

  // 서버데이터 호출
  const { v2ResourcesListUserStatusReport } = useFormeServer();
  const loading = ref(false);

  async function setKidReports() {
    try {
      loading.value = true;
      const data = await v2ResourcesListUserStatusReport(kid.value.UUID);

      if (isString(data)) {
        resetKidReports();
        console.error("useKidStore - setKidReports ::", data);

        return;
      }

      data.evaluation?.sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);

        if (aDate > bDate) {
          return 1;
        } else {
          return -1;
        }
      });

      data.state?.sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);

        if (aDate < bDate) {
          return 1;
        } else {
          return -1;
        }
      });

      kidReports.value = data;
    } catch (e) {
      console.error("useKidStore - setKidReports ::", e);
    } finally {
      loading.value = false;
    }
  }

  watch(
    () => kid.value,
    async () => {
      await setKidReports();
    }
  );

  return {
    getKid,
    setKid,
    resetKid,
    getNewKid,
    setNewKid,
    resetNewKid,
    getKidEmotions,
    setKidEmotions,
    resetKidEmotions,
    getKidSchedules,
    setKidSchedules,
    resetKidSchedules,
    getKidReports,
    loading,
    setKidReports,
  };
});
